// Filtros de Outbound Loads.
//
// LA MAYORÍA YA NO ESTÁ AQUÍ: vive en la cabecera de su columna
// (`CabeceraFiltrable`, repartidos por `filtrosDeColumna`). Esta barra se queda
// con los que NO tienen columna, porque colgarlos de una cabecera cualquiera
// sería mentir sobre lo que filtran:
//
//   · Artículo — la tabla no pinta la lista de artículos, sólo el recuento.
//   · Empresa  — no hay columna de empresa.
//   · «Sólo»   — aéreas, atrasadas y con incidencia cruzan varias columnas.
//   · Incluir expedidas — ni siquiera filtra: cambia lo que se le PIDE al
//     backend, así que no cuenta en `contarActivos` ni sale en los chips. Se
//     ve activo en el propio botón.
//
// NINGÚN FILTRO ESTÁ EN LOS DOS SITIOS. El estado es uno solo, pero dos
// controles sobre el mismo campo son dos sitios donde se descuadra el catálogo
// en cascada sin que nada dé error.
//
// LA FRANJA DE CHIPS SE QUEDA ENTERA, con los de las cabeceras incluidos. Es
// justo lo que hace que mudarlos sea seguro: la tabla mide 1.850 px y en un
// portátil hay columnas fuera de pantalla, así que sin los chips un filtro
// puesto en una columna que no se ve sería un filtro invisible.
//
// El rango de fecha de salida ACOTA qué cargas entran; las bandas por
// proximidad a hoy siguen agrupando lo que queda. Una filtra y otra ordena: no
// son dos maneras de decir lo mismo. Con rango puesto, una carga sin fecha no
// cabe en él (`enRangoFecha`).
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { CargaEstadoPicking, EstadoCarga } from '@/types'
import { MultiFiltro, type OpcionMulti } from '@/components/common/MultiFiltro'
import { etiquetaCatalogoDe } from '@/utils/filtroArticulo'
import { etiquetaHuecoDe, SIN_INFORMAR } from '@/utils/filtroHuecos'
import { BarraFiltrosActivos, filtrosDe, type FiltroActivo } from '@/components/common/BarraFiltrosActivos'
import { filterLabelCls, filterCardCls } from '@/components/common/FilterBar'

/** Cuatro estados, no tres. Antes «incompleta» metía en el mismo saco la carga
 *  que no tiene NADA reservado y la que tiene casi todo, que para quien prepara
 *  son dos situaciones distintas. */
export type FiltroReserva = '' | 'sin' | 'parcial' | 'completa'
export type FiltroPaletizada = 'si' | 'no' | 'noConsta'
export type FiltroMuelle = 'con' | 'sin'

export interface CargasSalidaFiltrosState {
  /** Nº de carga, del catálogo de las cargadas. En la cabecera de «Carga». */
  carga: string[]
  /** Nº de pedido. Una carga lleva varios: casa si alguno coincide. */
  pedido: string[]
  /** Cliente, elegido por NOMBRE; se filtra por su código. */
  cliente: string[]
  /** Códigos CRUDOS de artículo, elegidos del catálogo. Sin columna: en la barra. */
  articulo: string[]
  /**
   * El buscador libre de un solo campo que había antes. Ningún control lo
   * escribe ya: sobrevive para que los enlaces `?q=…` en circulación sigan
   * filtrando lo que filtraban, en vez de dejar de funcionar sin avisar.
   */
  terminos: string[]
  /** Rango de fecha de salida, ISO `yyyy-mm-dd`; `''` = sin límite. */
  desde: string
  hasta: string
  almacen: string[]
  /** Sin columna: se queda en la barra. */
  empresa: string[]
  /** Clasificación comercial del cliente; incluye el hueco como opción. */
  grupo: string[]
  /** Incoterm; incluye el hueco como opción. */
  incoterm: string[]
  /** Modo de transporte YA RESUELTO (`modoTransporteDe`), no el campo crudo:
   *  una carga marcada como aérea filtra por AEREO aunque su `modoTransporte`
   *  diga otra cosa, que es lo que la tabla enseña. Incluye el hueco. */
  modo: string[]
  estadoPicking: CargaEstadoPicking[]
  /** El estado de cabecera de la carga (`estadoDe`), que es el chip que pinta la
   *  columna «Estado». No se confunde con `estadoPicking`: aquél mira sólo el
   *  picking y éste además la reserva y si la carga ya salió. */
  estado: EstadoCarga[]
  reserva: FiltroReserva
  paletizada: FiltroPaletizada[]
  soloAereas: boolean
  muelle: FiltroMuelle[]
  soloAtrasadas: boolean
  soloIncidencia: boolean
  /** Va al endpoint, no filtra en cliente. */
  incluirExpedidas: boolean
}

export const EMPTY_FILTROS: CargasSalidaFiltrosState = {
  carga: [], pedido: [], cliente: [], articulo: [], terminos: [],
  desde: '', hasta: '',
  almacen: [], empresa: [], grupo: [], incoterm: [], modo: [],
  estadoPicking: [], estado: [], reserva: '', paletizada: [],
  soloAereas: false, muelle: [], soloAtrasadas: false, soloIncidencia: false,
  incluirExpedidas: false,
}

export function contarActivos(f: CargasSalidaFiltrosState): number {
  const listas = [
    f.carga, f.pedido, f.cliente, f.articulo, f.terminos,
    f.almacen, f.empresa, f.grupo, f.incoterm, f.modo,
    f.estadoPicking, f.estado, f.paletizada, f.muelle,
  ]
  return listas.filter(l => l.length > 0).length +
    (f.desde || f.hasta ? 1 : 0) + (f.reserva ? 1 : 0) +
    (f.soloAereas ? 1 : 0) + (f.soloAtrasadas ? 1 : 0) + (f.soloIncidencia ? 1 : 0)
}

interface Props {
  dark: boolean
  value: CargasSalidaFiltrosState
  onChange: (patch: Partial<CargasSalidaFiltrosState>) => void
  onClear: () => void
  opcionesArticulo: OpcionMulti[]
  /** No pinta ningún control aquí —el filtro vive en la cabecera de «Cliente /
   *  Pedido»—, pero sí hace falta para que su chip diga el NOMBRE del cliente
   *  en vez del código. */
  opcionesCliente: OpcionMulti[]
  empresas: string[]
  showEmpresa: boolean
  resumen?: string
}

function fechaCorta(iso: string): string {
  return iso ? iso.split('-').reverse().join('/') : '…'
}

function Toggle({
  dark, activo, texto, onClick,
}: { dark: boolean; activo: boolean; texto: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={`h-[30px] px-2.5 rounded-lg border text-[12px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
        activo
          ? dark
            ? 'border-sky-500/60 bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/15'
            : 'border-sky-400 bg-sky-50/60 text-sky-700 ring-1 ring-sky-300/25'
          : dark
            ? 'bg-slate-800 border-white/10 text-slate-300'
            : 'bg-white border-slate-200 text-slate-700'
      }`}
    >
      {texto}
    </button>
  )
}

/** Una fila de filtros, con su nombre a la izquierda.
 *
 *  El rótulo va muy apagado y alineado a la derecha: tiene que poder leerse si
 *  se busca y desaparecer si no. Es una señal de orientación, no un título. */
function Fila({ dark, nombre, children }: {
  dark: boolean; nombre: string; children: ReactNode
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <span
        className={`w-[76px] shrink-0 text-right ${filterLabelCls(dark)} ${
          dark ? 'text-slate-600' : 'text-slate-300'
        }`}
      >
        {nombre}
      </span>
      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">{children}</div>
    </div>
  )
}

export function CargasSalidaFiltros({
  dark, value, onChange, onClear,
  opcionesArticulo, opcionesCliente,
  empresas, showEmpresa, resumen,
}: Props) {
  const { t } = useTranslation()
  const sinInformar = t('common.filtros.sinInformar')

  const soloSeleccion = [
    ...(value.soloAereas ? ['aereas'] : []),
    ...(value.soloAtrasadas ? ['atrasadas'] : []),
    ...(value.soloIncidencia ? ['incidencia'] : []),
  ]
  const ponerSolo = (v: string[]) => onChange({
    soloAereas: v.includes('aereas'),
    soloAtrasadas: v.includes('atrasadas'),
    soloIncidencia: v.includes('incidencia'),
  })
  const etiquetaSolo = (v: string) => t(
    v === 'aereas' ? 'salesOrders.picking.aerea'
      : v === 'atrasadas' ? 'cargasSalida.filtros.soloAtrasadas'
      : 'cargasSalida.filtros.soloIncidencia',
  )

  // Los chips los lleva TODOS la barra, estén sus controles aquí o en una
  // cabecera: son la única vista completa de lo que hay puesto.
  const filtrosActivos: FiltroActivo[] = [
    ...filtrosDe(value.carga, carga => onChange({ carga }),
      { campo: t('cargasSalida.filtros.carga'), prefijo: 'c:' }),
    ...filtrosDe(value.pedido, pedido => onChange({ pedido }),
      { campo: t('cargasSalida.filtros.pedido'), prefijo: 'p:' }),
    ...filtrosDe(value.cliente, cliente => onChange({ cliente }), {
      campo: t('cargasSalida.filtros.cliente'),
      etiquetaDe: etiquetaCatalogoDe(opcionesCliente),
      prefijo: 'cli:',
    }),
    ...filtrosDe(value.articulo, articulo => onChange({ articulo }), {
      campo: t('cargasSalida.filtros.articulo'),
      etiquetaDe: etiquetaCatalogoDe(opcionesArticulo),
      prefijo: 'a:',
    }),
    // Los del buscador antiguo no llevan campo: no salen de ninguno en concreto,
    // y ponerles uno sería mentir sobre dónde buscó el enlace que los trajo.
    ...filtrosDe(value.terminos, terminos => onChange({ terminos }),
      { campo: t('common.busqueda.legado'), prefijo: 'q:' }),
    ...(value.desde || value.hasta ? [{
      clave: 'fechas', campo: t('cargasSalida.filtros.fechaSalida'),
      valor: [fechaCorta(value.desde), fechaCorta(value.hasta)].join(' – '),
      quitar: () => onChange({ desde: '', hasta: '' }),
    }] : []),
    ...filtrosDe(value.almacen, almacen => onChange({ almacen }),
      { campo: t('cargasSalida.filtros.almacen') }),
    ...filtrosDe(value.empresa, empresa => onChange({ empresa }), {
      campo: t('common.company'),
      etiquetaDe: (v: string) => v.toUpperCase(),
    }),
    ...filtrosDe(value.grupo, grupo => onChange({ grupo }), {
      campo: t('cargasSalida.filtros.grupoCliente'),
      etiquetaDe: etiquetaHuecoDe(sinInformar),
    }),
    ...filtrosDe(value.incoterm, incoterm => onChange({ incoterm }), {
      campo: t('cargasSalida.filtros.incoterm'),
      etiquetaDe: etiquetaHuecoDe(sinInformar),
    }),
    ...filtrosDe(value.modo, modo => onChange({ modo }), {
      campo: t('cargasSalida.filtros.modo'),
      etiquetaDe: (v: string) => (v === SIN_INFORMAR
        ? sinInformar
        : t(`cargasSalida.tabla.modos.${v}`, { defaultValue: v })),
    }),
    ...filtrosDe(value.estadoPicking, v => onChange({ estadoPicking: v as CargaEstadoPicking[] }), {
      campo: t('cargasSalida.filtros.estadoPicking'),
      etiquetaDe: (v: string) => t(`salesOrders.picking.estado.${v}`),
    }),
    ...(value.reserva ? [{
      clave: 'res', campo: t('cargasSalida.filtros.reserva'),
      valor: t(`cargasSalida.filtros.reserva${
        value.reserva === 'completa' ? 'Completa' : value.reserva === 'parcial' ? 'Parcial' : 'Sin'
      }`),
      quitar: () => onChange({ reserva: '' }),
    }] : []),
    ...filtrosDe(value.estado, v => onChange({ estado: v as EstadoCarga[] }), {
      campo: t('cargasSalida.tabla.columnas.estado'),
      etiquetaDe: (v: string) => t(`cargasSalida.estadoCarga.${v}`),
    }),
    ...filtrosDe(value.paletizada, v => onChange({ paletizada: v as FiltroPaletizada[] }), {
      campo: t('cargasSalida.filtros.paletizada'),
      etiquetaDe: (v: string) => t(
        v === 'si' ? 'salesOrders.picking.paletizada'
          : v === 'no' ? 'salesOrders.picking.noPaletizada'
          : 'cargasSalida.filtros.noConsta',
      ),
    }),
    ...filtrosDe(value.muelle, v => onChange({ muelle: v as FiltroMuelle[] }), {
      campo: t('cargasSalida.filtros.muelle'),
      etiquetaDe: (v: string) => t(
        v === 'con' ? 'cargasSalida.filtros.conMuelle' : 'cargasSalida.filtros.sinMuelle',
      ),
    }),
    ...filtrosDe(soloSeleccion, ponerSolo, {
      campo: t('cargasSalida.filtros.solo'),
      etiquetaDe: etiquetaSolo,
    }),
  ]

  const separador = dark ? 'border-white/[0.06]' : 'border-slate-100'

  return (
    <div className={filterCardCls(dark)}>
      <Fila dark={dark} nombre={t('cargasSalida.filtros.filaOtros')}>
        <div className="w-[210px]">
          <MultiFiltro
            dark={dark}
            opciones={opcionesArticulo}
            seleccion={value.articulo}
            onSeleccion={articulo => onChange({ articulo })}
            placeholder={t('cargasSalida.filtros.articulo')}
          />
        </div>

        {showEmpresa && (
          <div className="w-[124px]">
            <MultiFiltro
              dark={dark}
              opciones={empresas.map(v => ({ valor: v, titulo: v.toUpperCase() }))}
              seleccion={value.empresa}
              onSeleccion={empresa => onChange({ empresa })}
              placeholder={t('common.company')}
            />
          </div>
        )}

        {/* Los tres «sólo» eran tres interruptores y ocupaban una fila entera
            para tres sí/no que casi nunca están puestos. Por dentro siguen
            siendo tres booleanos con sus mismas claves de URL: un enlace
            guardado con `?atrasadas=1` sigue valiendo. */}
        <div className="w-[136px]">
          <MultiFiltro
            dark={dark}
            opciones={['aereas', 'atrasadas', 'incidencia'].map(v => ({
              valor: v, titulo: etiquetaSolo(v),
            }))}
            seleccion={soloSeleccion}
            onSeleccion={ponerSolo}
            placeholder={t('cargasSalida.filtros.solo')}
            titulosMono={false}
          />
        </div>

        {/* Dónde está el resto. Se dice una vez y en voz baja: los filtros se
            mudaron a las cabeceras y nadie va a buscarlos ahí por su cuenta. */}
        <span
          className={`flex items-center gap-1.5 text-[11px] ${dark ? 'text-slate-600' : 'text-slate-600'}`}
        >
          <svg
            className="w-3 h-3 shrink-0"
            viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
          >
            <path d="M2.2 3h11.6l-4.4 5.1v4.6l-2.8 1.5V8.1L2.2 3z" />
          </svg>
          {t('cargasSalida.filtros.pistaCabeceras')}
        </span>

        <div className="ml-auto">
          <Toggle
            dark={dark}
            activo={value.incluirExpedidas}
            texto={t('cargasSalida.filtros.incluirExpedidas')}
            onClick={() => onChange({ incluirExpedidas: !value.incluirExpedidas })}
          />
        </div>
      </Fila>

      {filtrosActivos.length > 0 && (
        <div className={`px-3 pb-3 pt-2.5 border-t ${separador}`}>
          <BarraFiltrosActivos dark={dark} filtros={filtrosActivos} onLimpiar={onClear} resumen={resumen} />
        </div>
      )}
    </div>
  )
}
